/**
 * ENVIRONMENT: PRODUCTION
 * MODULE: Central BBPS Dispute & Complaint Management Console (B2C)
 * PATH: src/routes/payout/TransactionPayInList.js
 * COMPLIANCE: NPCI BBPS CMS (Complaint Management System), DPDP Act 2023
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Space,
  Row,
  Col,
  Card,
  Tag,
  Tooltip,
  Typography,
  Modal,
  Input,
  Select,
  DatePicker,
  message,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  CopyOutlined,
  CustomerServiceOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
  EyeOutlined,
  EyeInvisibleOutlined,
  EditOutlined,
  ThunderboltFilled,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { parseQueryParams, stringifyQueryParams } from "utils/url";
import api from "utils/api";
import { ComplaintList } from "requests/statistic";
import { format } from "date-fns";
import "./TransactionPayInList.css";

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

function TransactionPayInList() {
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(
    Number(process.env.REACT_APP_RECORDS_PER_PAGE) || 10
  );
  const [totalCount, setTotalCount] = useState(0);
  const [records, setRecords] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Status Change Modal
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [modalStatus, setModalStatus] = useState("");
  const [modalRemark, setModalRemark] = useState("");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // DPDP Masking State
  const [revealedEmails, setRevealedEmails] = useState({});

  const searchRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Load Complaints Records
  const getData = useCallback(
    async (query) => {
      try {
        setIsTableLoading(true);
        const activeQuery = query || parseQueryParams(location);
        const response = await ComplaintList(activeQuery);

        if (response && response.data) {
          setRecords(response.data);
          setPage(Number(response.current_page) || 1);
          setPerPage(Number(response.per_page) || 10);
          setTotalCount(Number(response.total) || 0);
        } else {
          setRecords([]);
          setTotalCount(0);
        }
      } catch (err) {
        message.error(err?.message || "Failed to load complaint records.");
      } finally {
        setIsTableLoading(false);
      }
    },
    [location]
  );

  useEffect(() => {
    const query = parseQueryParams(location);
    if (query.payment_status) setStatusFilter(query.payment_status);
    if (query.keyword) setSearchTerm(query.keyword);
    getData(query);
  }, [location, getData]);

  // DPDP Email Masking
  const maskEmail = (email) => {
    if (!email || !email.includes("@")) return "N/A";
    const [user, domain] = email.split("@");
    if (user.length <= 2) return `${user}•••@${domain}`;
    return `${user.slice(0, 2)}${"•".repeat(Math.min(user.length - 2, 6))}@${domain}`;
  };

  // DPDP Mobile Masking
  const maskMobile = (mobile) => {
    if (!mobile || String(mobile).length < 7) return mobile || "N/A";
    const str = String(mobile);
    return `${str.slice(0, 4)}••••${str.slice(-2)}`;
  };

  // Copy helper
  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  // Search Filter
  const handleSearch = (value) => {
    let query = parseQueryParams(location);
    query = {
      ...query,
      page: 1,
      keyword: value.trim(),
    };
    if (!value.trim()) delete query.keyword;

    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams(query),
    });
  };

  // Status Filter
  const handleStatusFilterChange = (val) => {
    setStatusFilter(val);
    let query = parseQueryParams(location);
    if (val) {
      query = { ...query, page: 1, payment_status: val };
    } else {
      delete query.payment_status;
    }
    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams(query),
    });
  };

  // Date Range Filter
  const handleDateChange = (dates) => {
    let query = parseQueryParams(location);
    if (dates && dates.length === 2) {
      query = {
        ...query,
        page: 1,
        created_at_date_min: dayjs(dates[0]).format("YYYY-MM-DD"),
        created_at_date_max: dayjs(dates[1]).format("YYYY-MM-DD"),
      };
    } else {
      delete query.created_at_date_min;
      delete query.created_at_date_max;
    }
    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams(query),
    });
  };

  // Table Pagination
  const onChangeTable = (pagination) => {
    let query = parseQueryParams(location);
    query = {
      ...query,
      page: pagination.current,
      per_page: pagination.pageSize,
    };
    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams(query),
    });
  };

  // Status Update Trigger Modal
  const openStatusModal = (id, currentStatus) => {
    setCurrentId(id);
    setModalStatus(currentStatus || "");
    setModalRemark("");
    setIsStatusModalVisible(true);
  };

  const handleStatusSubmit = async () => {
    if (!currentId) return;
    if (!modalStatus) {
      message.error("Please select a target resolution state.");
      return;
    }

    try {
      setIsUpdatingStatus(true);
      const formData = new FormData();
      formData.append("id", currentId);
      formData.append("status", modalStatus);
      formData.append("remark", modalRemark || "");

      const res = await api.post("/admin/complain-status-update", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.status === "1" || res.data?.status === 1 || res.status === 200) {
        message.success("Complaint resolution state updated.");
        setIsStatusModalVisible(false);
        getData();
      } else {
        message.error(res.data?.message || "Failed to update complaint status.");
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Error submitting complaint status update.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Status Tag Formatter
  const renderStatusBadge = (status) => {
    const s = (status || "").toLowerCase();
    switch (s) {
      case "closed":
      case "success":
      case "resolved":
        return (
          <Tag icon={<CheckCircleFilled />} color="success" className="complaint-status-tag">
            {status?.toUpperCase() || "RESOLVED"}
          </Tag>
        );
      case "inprocess":
      case "pending":
        return (
          <Tag icon={<ClockCircleFilled />} color="warning" className="complaint-status-tag">
            IN-PROCESS
          </Tag>
        );
      case "failed":
      case "rejected":
        return (
          <Tag icon={<CloseCircleFilled />} color="error" className="complaint-status-tag">
            FAILED
          </Tag>
        );
      default:
        return <Tag color="default">{status?.toUpperCase() || "UNKNOWN"}</Tag>;
    }
  };

  const columns = [
    {
      title: "SR",
      width: 60,
      align: "center",
      render: (_, __, index) => (
        <span className="complaint-sr">{(page - 1) * perPage + index + 1}</span>
      ),
    },
    {
      title: "Date / Time (IST)",
      width: 170,
      render: (_, record) => (
        <div className="meta-time-cell">
          <span className="complaint-date">
            {record.date_time
              ? format(new Date(record.date_time), "dd MMM yyyy")
              : "N/A"}
          </span>
          <span className="complaint-time">
            {record.date_time
              ? format(new Date(record.date_time), "hh:mm a")
              : ""}
          </span>
        </div>
      ),
    },
    {
      title: "Customer Details",
      width: 240,
      render: (_, record) => {
        const email = record.user_detail?.email;
        const mobile = record.user_detail?.mobile;
        const isRevealed = revealedEmails[record.id || record._id];

        return (
          <div className="customer-meta-cell">
            <div className="email-row">
              <span className="customer-email">
                {isRevealed ? email : maskEmail(email)}
              </span>
              {email && (
                <Button
                  type="text"
                  size="small"
                  className="unmask-toggle-btn"
                  icon={isRevealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  onClick={() =>
                    setRevealedEmails((prev) => ({
                      ...prev,
                      [record.id || record._id]: !prev[record.id || record._id],
                    }))
                  }
                />
              )}
            </div>
            <div className="mobile-row">
              <span className="customer-phone">
                +91 {isRevealed ? mobile : maskMobile(mobile)}
              </span>
            </div>
            <Tag color="cyan" className="customer-role-tag">
              {record.user_detail?.role || "B2C Consumer"}
            </Tag>
          </div>
        );
      },
    },
    {
      title: "Complaint Tracking ID",
      width: 220,
      render: (_, record) => (
        <div className="ref-track-cell">
          <span className="ref-track-id">{record.complaintId || "N/A"}</span>
          {record.complaintId && (
            <Tooltip title="Copy Complaint ID">
              <CopyOutlined
                className="copy-ref-icon"
                onClick={() => copyToClipboard(record.complaintId, "Complaint ID")}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Transaction Ref ID",
      width: 230,
      render: (_, record) => (
        <div className="ref-track-cell">
          <span className="ref-track-id txn-color">{record.txnRefId || "N/A"}</span>
          {record.txnRefId && (
            <Tooltip title="Copy Txn ID">
              <CopyOutlined
                className="copy-ref-icon"
                onClick={() => copyToClipboard(record.txnRefId, "Transaction ID")}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Dispute Status",
      width: 140,
      align: "center",
      render: (_, record) => renderStatusBadge(record.status),
    },
    {
      title: "Actions",
      width: 130,
      align: "right",
      render: (_, record) => (
        <Button
          size="middle"
          icon={<EditOutlined />}
          onClick={() => openStatusModal(record._id || record.id, record.status)}
          className="update-status-btn"
        >
          Update State
        </Button>
      ),
    },
  ];

  return (
    <div className="complaint-console-container">
      {/* Enterprise Header Card */}
      <Card className="complaint-header-card" bordered={false}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <div className="complaint-title-wrap">
              <CustomerServiceOutlined className="header-dispute-icon" />
              <div>
                <Title level={4} className="complaint-main-heading">
                  BBPS Dispute &amp; Complaint Management
                </Title>
                <Text type="secondary" className="complaint-subtext">
                  NPCI CMS dispute escalation, transaction reconciliation &amp; customer grievances
                </Text>
              </div>
            </div>
          </Col>

          <Col xs={24} lg={16} className="complaint-controls-wrap">
            <Space wrap size="middle" className="controls-flex">
              {/* Status Filter */}
              <Select
                placeholder="All Resolution States"
                value={statusFilter || undefined}
                onChange={handleStatusFilterChange}
                allowClear
                className="complaint-status-select"
              >
                <Option value="success">Resolved / Success</Option>
                <Option value="closed">Closed</Option>
                <Option value="pending">In-Process / Pending</Option>
                <Option value="failed">Failed / Rejected</Option>
              </Select>

              {/* Universal Search Input */}
              <Input
                placeholder="Search Complaint ID, Txn Ref, Email..."
                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                defaultValue={searchTerm}
                onPressEnter={(e) => handleSearch(e.target.value)}
                allowClear
                className="complaint-search-input"
              />

              {/* Date Range Picker */}
              <RangePicker
                onChange={handleDateChange}
                className="complaint-range-picker"
              />

              {/* Refresh Trigger */}
              <Button
                icon={<ReloadOutlined />}
                onClick={() => getData()}
                className="complaint-refresh-btn"
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Main Disputes Table */}
      <Card className="complaint-table-card" bordered={false}>
        <Table
          loading={isTableLoading}
          dataSource={records}
          columns={columns}
          onChange={onChangeTable}
          rowKey={(record) => record.id || record._id}
          pagination={{
            pageSize: perPage,
            total: totalCount,
            current: page,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} dispute tickets`,
          }}
          scroll={{ x: 1000 }}
          className="enterprise-complaint-table"
        />
      </Card>

      {/* Status Modification Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
            <ThunderboltFilled style={{ color: "#0284c7" }} />
            <span>Update Complaint Resolution State</span>
          </div>
        }
        open={isStatusModalVisible}
        onOk={handleStatusSubmit}
        confirmLoading={isUpdatingStatus}
        onCancel={() => setIsStatusModalVisible(false)}
        okText="Save Resolution"
        className="complaint-modal"
      >
        <div style={{ marginTop: 12 }}>
          <Text strong style={{ fontSize: 12, color: "#334155", display: "block", marginBottom: 6 }}>
            Resolution State
          </Text>
          <Select
            value={modalStatus}
            onChange={(val) => setModalStatus(val)}
            style={{ width: "100%" }}
            placeholder="Select state"
          >
            <Option value="closed">Closed / Resolved</Option>
            <Option value="success">Success (Settled with Biller)</Option>
            <Option value="inprocess">In-Process (Under Biller Review)</Option>
            <Option value="failed">Failed / Rejected</Option>
          </Select>
        </div>

        <div style={{ marginTop: 16 }}>
          <Text strong style={{ fontSize: 12, color: "#334155", display: "block", marginBottom: 6 }}>
            Investigation Remark / Audited Reason
          </Text>
          <TextArea
            rows={3}
            placeholder="Enter resolution details, clearing RRN reference, or bank response..."
            value={modalRemark}
            onChange={(e) => setModalRemark(e.target.value)}
            style={{ borderRadius: 8 }}
          />
        </div>
      </Modal>
    </div>
  );
}

export default TransactionPayInList;